import { ArrowRightIcon, LockIcon } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { useEmbeddedOnboarding } from 'components/hooks/employer_onboarding/useEmbeddedOnboarding'
import { useOnboardingStep } from 'components/hooks/employer_onboarding/useOnboardingStep'
import { Banner, BorderedRadioButton, Button, TextLink, usePageTitle } from 'slab'

import { useCensus } from '../CensusProvider'
import { CenteredLayout } from '../CenteredLayout'
import { useEmployer } from '../EmployerProvider'
import { usePrefill } from '../PrefillProvider'
import { emitEmployerOnboardingEvent } from '../utils'
import { CSVUpload } from './census/CSVUpload'
import { ManualEntry } from './census/ManualEntry'
import { PayrollConnection } from './census/PayrollConnection'

export function CensusPage () {
  const navigate = useNavigate()
  usePageTitle('Tell us about your team')
  const { getPrefillValue } = usePrefill()

  const [selectedOption, setSelectedOption] = useState<
    'manual' | 'payroll' | 'csv' | null
  >(getPrefillValue('census_format') ?? null)

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    null
  )

  const { isEmbedded } = useEmbeddedOnboarding()
  const { employer } = useEmployer()
  const { census, refetch: refetchCensus } = useCensus()
  const { navigateToPreviousStep, navigateToNextStep } = useOnboardingStep()

  // TODO: get this value (feature flag?)
  // Always true in embedded mode for now
  const prospectId = employer?.quoting_prospect_id

  useEffect(() => {
    // Sanity check - in embedded onboarding, we redirect to dependents-only flow to avoid overwriting
    if (isEmbedded) {
      navigate('/employers/onboarding/setup/census/embedded', { replace: true })
    }
  }, [isEmbedded])

  useEffect(() => {
    switch (census?.format) {
      case 'generic':
        setSelectedOption('csv')
        break
      case 'manual_entry':
        setSelectedOption('manual')
        break
    }
  }, [census])

  const handleSuccess = useCallback(async () => {
    await refetchCensus()

    const censusType = selectedOption === 'manual' ? 'manual' : 'upload'
    emitEmployerOnboardingEvent('[ER] Census added', {
      census_type: censusType
    })

    navigateToNextStep()
  }, [refetchCensus, selectedOption])

  function renderSelectedOption () {
    switch (selectedOption) {
      // We've removed the payroll connection option from the form for now but
      // plan to add it back in the future, so we're leaving the rest of the
      // code for it in place for now.
      case 'payroll':
        return <PayrollConnection onSuccess={handleSuccess} />
      case 'csv':
        return (
          <CSVUpload
            census={census?.format === 'generic' ? census : undefined}
            onSuccess={handleSuccess}
            buttonPortalContainer={portalContainer}
          />
        )
      case 'manual':
        return (
          <ManualEntry
            census={census?.format === 'manual_entry' ? census : undefined}
            onSuccess={handleSuccess}
            buttonPortalContainer={portalContainer}
          />
        )
      default:
        return null
    }
  }

  function renderCensusOptions () {
    if (!prospectId) {
      // TODO: What should we actually do if a user gets here and their employer
      // doesn't have a prospect? This could happen if a user started onboarding
      // on v1 and resumed on v2.
      return (
        <Banner type='warning' title='Cannot upload censuses for this employer'>
          We cannot upload censuses for the current employer because it is
          missing a prospect. Please contact support.
        </Banner>
      )
    }

    return (
      <>
        <>
          <fieldset aria-label='How would you like to add employees?'>
            <div className='grid xs:grid-cols-2 gap-3'>
              <BorderedRadioButton
                description='Add all employees from a .csv file'
                label='Upload a list of your employees'
                name='selected_option'
                checked={selectedOption === 'csv'}
                onChange={() => setSelectedOption('csv')}
              />
              <BorderedRadioButton
                description='Manually enter employee data'
                label='Manual entry'
                name='selected_option'
                checked={selectedOption === 'manual'}
                onChange={() => setSelectedOption('manual')}
              />
            </div>
          </fieldset>
          {!!selectedOption && <hr />}
        </>

        {renderSelectedOption()}
      </>
    )
  }

  const heading = 'To calculate allowances, we need to know a little more about your team'
  const subheading = 'You can upload a list of employees or add them individually below.'

  return (
    <CenteredLayout displaySupportLink>
      <CenteredLayout.Header
        heading={heading}
        subheading={subheading}
      />
      <CenteredLayout.Container>
        {renderCensusOptions()}
      </CenteredLayout.Container>
      <CenteredLayout.Buttons>
        <Button variant='secondary' onClick={navigateToPreviousStep}>
          Back
        </Button>
        {/**
         * The components for CSV upload and manual entry will own their own
         * "next" buttons. We just need to set up an element for them
         * to portal into.
         **/}
        {selectedOption === 'manual' || selectedOption === 'csv'
          ? (
            <div ref={setPortalContainer} />
            )
          : (
            <Button
              variant='primary'
              disabled
              onClick={() => handleSuccess()}
              rightIcon={ArrowRightIcon}
            >
              Next step
            </Button>
            )}
      </CenteredLayout.Buttons>
      <div className='mt-10 text-left flex items-start'>
        <LockIcon size={20} className='text-gray-500 mr-2 shrink-0' />
        <p className='text-gray-500 text-left'>
          Thatch does not sell your data in any way, shape, or form. Our SOC 2
          Type II certification means the data you share with Thatch is
          protected and secure. Learn more at our{' '}
          <TextLink
            href='https://trust.thatch.com/'
            target='_blank'
            rel='noopener noreferrer'
          >
            Trust Center.
          </TextLink>
        </p>
      </div>
    </CenteredLayout>
  )
}
